import React, { useState, useEffect } from "react";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { Label } from "@progress/kendo-react-labels";
import { Input, RadioGroup, TextArea } from "@progress/kendo-react-inputs";
import { Checkbox } from "@progress/kendo-react-inputs";
import { ComboBox } from "@progress/kendo-react-dropdowns";
import { filterBy } from "@progress/kendo-data-query";
import { TabStrip, TabStripTab } from "@progress/kendo-react-layout";
import { PageTabItemTitle } from "components/tabs/TabItem/PageTabItemTitle";
import { Button } from "@progress/kendo-react-buttons";
import { Loader } from "@progress/kendo-react-indicators";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { TransactionTypes } from "components/Enums/TransactionTypes.ts";
import { Form } from "@progress/kendo-react-form";
import AddressInputComponent from "components/common/AddressInputComponent";
import SubmitButton from "components/common/SubmitButton";
import { useActiveVats } from "services/vatService";
import { useActiveBranches } from "services/branchService";
import { getSupplierCategories } from "services/supplierCategoryService";
import { useNominalsByType } from "services/nominalsService";
import {
    addSupplier,
    updateSupplier,
    fetchSupplierById,
} from "services/supplierService";
import { useCompany } from "services/companyService";
import { DocumentList } from "components/Documents/DocumentList";
import { useAccessRight, AccountsAccessRights } from "../../components/UserAccessRights.ts";

const data = [
    {
        label: "Disbursement Only",
        value: TransactionTypes.Disbursement,
    },
    {
        label: "Purchase Only",
        value: TransactionTypes.Purchase,
    },
    {
        label: "Both",
        value: TransactionTypes.All,
    },
];

const AddNewSupplier = (props) => {
    const [view, setView] = useState(props.viewType === "Add" ? "Add" : "View");
    const [selected, setSelected] = useState(0);
    const [error, setError] = useState(false);
    const [supplierId, setSupplierId] = useState();
    const [isLoadingSupplier, setIsLoadingSupplier] = useState(
        () => props.viewType !== "Add" && Boolean(props.supplierId),
    );
    const tabs =
        props.viewType === "Add"
            ? ["Details", "Defaults", "Bank Info"]
            : ["Details", "Defaults", "Bank Info", "Documents"];
    const [onHold, setOnHold] = useState(false);
    const [payWhenPaid, setPayWhenPaid] = useState(false);
    const [archived, setArchivedValue] = useState(false);
    const [supplierRef, setSupplierRef] = useState();
    const [supplierName, setSupplierName] = useState();
    const [type, setType] = useState();
    const [contactID, setContactID] = useState();
    const [vatCodeId, setVatCodeId] = useState(null);
    const [selectedBranch, setSelectedBranch] = useState(null);
    const [selectedNominal, setSelectedNominal] = useState(null);
    const [vatNumber, setVatNumber] = useState();
    const [creditLimit, setCreditLimit] = useState();
    const [termDays, setTermDays] = useState("");
    const [notes, setNotes] = useState();
    const [payeeName, setPayeeName] = useState();
    const [payeeTouched, setPayeeTouched] = useState(false);
    const [email1, setEmail1] = useState("");
    const [transactionTypes, setTransactionTypes] = useState();
    const [balance, setBalance] = useState();
    const [branchData, setBranchData] = useState([]);
    const [address, setAddress] = useState({});
    const [bankAccount, setBankAccount] = useState({
        bankName: "",
        accountType: "",
        accountTitle: "",
        accountNo: "",
        sortCode: "",
        iban: "",
        bacsPayment: false,
        bacsCode: "",
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { data: activevatsdata = [], isLoading: vatsLoading } = useActiveVats();
    const { data: activeBranches = [], loading: branchesLoading } = useActiveBranches();
    const { data: companyData } = useCompany();
    const [vatsdata, setvatsData] = useState();
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const { data: nominalsData = [], loading: nominalsLoading } = useNominalsByType(
        "ProfitAndLoss");
    const [filteredNominals, setFilteredNominals] = useState([]);
    const canEditSupplier = useAccessRight(AccountsAccessRights.ModifySuppliers);

    useEffect(() => {
        if (activevatsdata && activevatsdata?.length > 0) {
            setvatsData(activevatsdata.slice());
        }
    }, [activevatsdata?.length]);

    useEffect(() => {
        if (props.viewType !== "Add") return;
        setTransactionTypes(TransactionTypes.All);
    }, [props.viewType]);

    useEffect(() => {
        if (props.viewType !== "Add") return;
        if (!companyData) return;

        const defaultVatCodeId = companyData?.defaultVatCodeId;

        if (defaultVatCodeId && activevatsdata?.length > 0 && !vatCodeId) {
            const vat = activevatsdata.find((v) => v.vatCodeId === defaultVatCodeId);
            if (vat) setVatCodeId(vat);
        }
    }, [
        props.viewType,
        companyData,
        activevatsdata?.length,
        vatCodeId,
    ]);

    useEffect(() => {
        if (nominalsData && nominalsData?.length > 0) {
            setFilteredNominals(nominalsData.slice());
        }
    }, [nominalsData?.length]);

    const hasBalance = Number(balance ?? 0) !== 0;
    const isArchiveBlocked = view === "Edit" && hasBalance;
    const archiveDisabledTooltipText = isArchiveBlocked
        ? "Cannot archive supplier with balances."
        : "";

    const changeArchivedValue = (e) => {
        if (view === "View") {
            return;
        }
        setArchivedValue(!archived);
        if (onHold) {
            setOnHold(false);
        }
    };
    const changePayWhenPaid = (e) => {
        if (view === "View") {
            return;
        }
        const newPayWhenPaid = !payWhenPaid;
        setPayWhenPaid(newPayWhenPaid);

        // If "Pay When Paid" is checked, set term days to 0
        if (newPayWhenPaid) {
            setTermDays(0);
        }
    };
    const changeOnHold = (e) => {
        if (view === "View") {
            return;
        }
        setOnHold(!onHold);
        if (archived) {
            setArchivedValue(false);
        }
    };

    useEffect(() => {
        const loadCategories = async () => {
            try {
                const data = await getSupplierCategories();
                setCategories(data);
            } catch (err) {
                console.error("Failed to fetch categories", err);
            }
        };

        loadCategories();
    }, []);

    const vatCodeReturner = (id) => {
        if (!id) return null;
        return (
            activevatsdata.find((item) => item.vatCodeId === id) || {
                vatCodeId: id,
                display: String(id),
            }
        );
    };

    const branchReturner = (branchId, branchName) => {
        if (branchId == null) return null;
        return (
            activeBranches.find((item) => item.branchId === branchId) || {
                branchId,
                branchName: branchName || String(branchId),
            }
        );
    };

    const nominalReturner = (nominalRef) => {
        if (!nominalsData || !nominalsData.length) return null;
        return nominalsData.find((item) => item.nominalRef === nominalRef) || null;
    };

    const handleTermDaysChange = (value) => {
        if (view === "View") return;
        setPayWhenPaid(false);
        setTermDays(value);
    };

    const submitSupplier = async (e) => {
        e?.preventDefault?.();
        if (isSubmitting) return;

        if (!supplierRef || !supplierName || !transactionTypes) {
            setError(true);
            return;
        }

        const payload = {
            supplierRef,
            supplierName,
            type: parseInt(type),
            contactID: parseInt(contactID),
            vatCodeId: vatCodeId?.vatCodeId ? parseInt(vatCodeId.vatCodeId) : null,
            branchId: selectedBranch?.branchId ?? -1,
            nominalRef: selectedNominal?.nominalRef,
            vatNumber,
            creditLimit: parseInt(creditLimit),
            termDays: parseInt(termDays),
            notes,
            payeeName,
            email1,
            categoryId: selectedCategory || -1,
            transactionTypes,
            balance,
            onHold,
            payWhenPaid,
            archived,
            address,
            bankAcccount: {
                bankName: bankAccount.bankName || "",
                accountType: bankAccount.accountType || "",
                accountTitle: bankAccount.accountTitle || "",
                accountNo: bankAccount.accountNo || "",
                sortCode: bankAccount.sortCode || "",
                currencyCode: "",
                iban: bankAccount.iban || "",
                bacsPayment: Boolean(bankAccount.bacsPayment),
                bacsCode: bankAccount.bacsCode || "",
            },
        };

        setIsSubmitting(true);
        try {
            if (view === "Add") {
                const result = await addSupplier(payload);
                if (result?.success) {
                    setError(false);
                    props.onSupplierAdded?.();
                    props.onClose();
                }
            } else {
                const result = await updateSupplier(supplierId, payload);
                if (result?.success) {
                    props.refresh?.();
                    props.onClose();
                    setError(false);
                }
            }
        } catch (err) {
            console.error("Failed to submit supplier:", err);
        } finally {
            setIsSubmitting(false);
        }
    };

    const changeTransactionType = (e) => {
        if (view === "View") {
            return;
        }
        setTransactionTypes(e.value);
    };
    useEffect(() => {
        const loadSupplier = async () => {
            if (props.viewType === "Add" || !props.supplierId) {
                setIsLoadingSupplier(false);
                return;
            }

            setIsLoadingSupplier(true);
            try {
                const data = await fetchSupplierById(props.supplierId);
                if (!data) return;

                setView("View");
                setSupplierId(data.id);
                setOnHold(data.onHold);
                setPayWhenPaid(data.payWhenPaid);
                setArchivedValue(data.archived);
                setSupplierRef(data.supplierRef);
                setSupplierName(data.supplierName);
                setType(data.type);
                setContactID(data.contactID);
                setVatCodeId(vatCodeReturner(data.vatCodeId));
                setSelectedBranch(branchReturner(data.branchId, data.branchName));
                setSelectedNominal(nominalReturner(data.nominalRef));
                setVatNumber(data.vatNumber);
                setCreditLimit(data.creditLimit);
                setTermDays(data.termDays);
                setNotes(data.notes);
                setPayeeName(data.payeeName);
                setEmail1(data.email1 || "");
                setSelectedCategory(data.categoryId);
                setTransactionTypes(data.transactionTypes);
                setBalance(data.balance);
                setAddress(data.address);
                const bank = data.bankAcccount || data.bankAccount || {};
                setBankAccount({
                    bankName: bank.bankName || "",
                    accountType: bank.accountType || "",
                    accountTitle: bank.accountTitle || "",
                    accountNo: bank.accountNo || "",
                    sortCode: bank.sortCode || "",
                    iban: bank.iban || "",
                    bacsPayment: Boolean(bank.bacsPayment),
                    bacsCode: bank.bacsCode || "",
                });
            } catch (err) {
                console.error("Failed to load supplier:", err);
            } finally {
                setIsLoadingSupplier(false);
            }
        };

        loadSupplier();
    }, [props.supplierId, props.viewType]);

    useEffect(() => {
        if (activeBranches) {
            setBranchData(activeBranches.slice());
        }
    }, [activeBranches]);

    useEffect(() => {
        if (props.viewType === "Add") return;
        if (selectedBranch?.branchId == null) return;
        if (!activeBranches?.length) return;

        const matchedBranch = activeBranches.find(
            (item) => item.branchId === selectedBranch.branchId,
        );
        if (matchedBranch) {
            setSelectedBranch(matchedBranch);
        }
    }, [props.viewType, selectedBranch?.branchId, activeBranches]);

    useEffect(() => {
        if (props.viewType === "Add") return;
        if (!vatCodeId?.vatCodeId) return;
        if (!activevatsdata?.length) return;

        const matchedVat = activevatsdata.find(
            (item) => item.vatCodeId === vatCodeId.vatCodeId,
        );
        if (matchedVat) {
            setVatCodeId(matchedVat);
        }
    }, [props.viewType, vatCodeId?.vatCodeId, activevatsdata]);

    const filterData = (filter) => {
        const datas = activevatsdata.slice();
        return filterBy(datas, filter);
    };
    const branchFilterData = (filter) => {
        const branches = activeBranches.slice();
        return filterBy(branches, filter);
    };
    const filterChange = (event) => {
        setvatsData(filterData(event.filter));
    };
    const branchFilterChange = (event) => {
        setBranchData(branchFilterData(event.filter));
    };

    const nominalsFilterData = (filter) => {
        const nominals = nominalsData.slice();
        return filterBy(nominals, filter);
    };

    const nominalsFilterChange = (event) => {
        setFilteredNominals(nominalsFilterData(event.filter));
    };

    const updateBankAccount = (field, value) => {
        if (view === "View") return;
        setBankAccount((prev) => ({ ...prev, [field]: value }));
    };

    const DetailsTab = () => (
        <div>
            <div className="d-flex" style={{ gap: "25%" }}>
                <div>
                    <div>
                        <Label className="mb-1">Supplier Ref&nbsp;</Label>
                        <div className="">
                            <Input
                                style={{
                                    border: error && !supplierRef ? "1px solid red" : "",
                                    minWidth: "300px",
                                    width: "300px",
                                }}
                                value={supplierRef}
                                readOnly={view === "View"}
                                onChange={(e) => {
                                    setSupplierRef(e.target.value);
                                }}
                                placeholder={"Enter Supplier Ref"}
                            />
                        </div>
                    </div>
                    <div className="mt-3">
                        <Label className="mb-1">Supplier Name&nbsp;</Label>
                        <Input
                            style={{
                                border: error && !supplierName ? "1px solid red" : "",
                            }}
                            value={supplierName}
                            placeholder={"Enter Supplier Name"}
                            readOnly={view === "View"}
                            onChange={(e) => {
                                setSupplierName(e.target.value);
                            }}
                            onBlur={() => {
                                if (view !== "Add") return;
                                if (payeeTouched) return;
                                if (supplierName && supplierName !== payeeName) {
                                    setPayeeName(supplierName);
                                }
                            }}
                        />
                    </div>
                    <div className="mt-3">
                        <div>
                            <Label className="mb-1">Payee Name&nbsp;</Label>
                        </div>
                        <Input
                            value={payeeName}
                            readOnly={view === "View"}
                            placeholder={"Enter Payee Name"}
                            onChange={(e) => {
                                setPayeeName(e.target.value);
                                setPayeeTouched(true);
                            }}
                        />
                    </div>


                </div>
                <div>
                    <div className="mt-3">
                        <div className="label">
                            <Label>Transaction Types:&nbsp;</Label>
                        </div>
                        <div className={`mt-3 ${error && !transactionTypes ? "error" : ""}`}>
                            <RadioGroup
                                data={data}
                                readOnly={view === "View"}
                                className="gap-2 radio"
                                value={transactionTypes}
                                onChange={changeTransactionType}
                            />
                        </div>
                    </div>

                </div>
            </div>
            <div className="d-flex justify-content-between mt-3">
                <div>
                    <div>
                        <Label className="mb-1">Email Address&nbsp;</Label>
                    </div>
                    <Input
                        type="email"
                        value={email1}
                        readOnly={view === "View"}
                        placeholder={"Enter Email Address"}
                        onChange={(e) => {
                            setEmail1(e.target.value);
                        }}
                        style={{ width: "300px" }}
                    />
                </div>
                <div style={{ marginRight: "10%" }}>
                    <div>
                        <Label className="mb-1">Category&nbsp;</Label>
                    </div>
                    <ComboBox
                        data={categories}
                        textField="categoryName"
                        dataItemKey="supplierCategoryId"
                        disabled={view === "View"}
                        value={categories.find(
                            (cat) => cat.supplierCategoryId === selectedCategory,
                        )}
                        onChange={(e) => {
                            setSelectedCategory(e.value?.supplierCategoryId || -1);
                        }}
                        placeholder="Select Category"
                        iconClassName="wa"
                        style={{ width: "300px" }}
                    />
                </div>
            </div>
            <div className="mt-3">
                <Label className="label mb-2">Notes&nbsp;</Label>
                <div>
                    <TextArea
                        value={notes}
                        style={{ maxWidth: "none", width: "90%", height: "60px" }}
                        placeholder={"Enter Details"}
                        readOnly={view === "View"}
                        onChange={(e) => {
                            setNotes(e.target.value);
                        }}
                    />
                </div>
            </div>
            <div className="mt-3">
                <div className="d-flex radio">
                    <Checkbox
                        defaultChecked={false}
                        onClick={changeOnHold}
                        value={onHold}
                        label={"On Hold"}
                    />
                </div>
            </div>
            <div className="mt-3">
                <div className="d-flex radio">
                    <Checkbox
                        defaultChecked={false}
                        disabled={isArchiveBlocked}
                        className={isArchiveBlocked ? "k-disabled" : ""}
                        value={archived}
                        onClick={changeArchivedValue}
                        label={
                            <span style={{ display: "inline-block" }}>
                                <Tooltip anchorElement="target" position="top" parentTitle={true}>
                                    <span
                                        className={isArchiveBlocked ? "k-disabled" : ""}
                                        title={archiveDisabledTooltipText}
                                        style={{
                                            display: "inline",
                                            whiteSpace: "nowrap",
                                            pointerEvents: "auto",
                                        }}>
                                        Archived
                                    </span>
                                </Tooltip>
                            </span>
                        }
                    />
                </div>
            </div>
        </div>
    );
    const DefaultsTab = () => (
        <div>
            <div className="d-flex">
                <div className="col-5 me-5 ">
                    <div className="mt-3">
                        <Label className="mb-1 d-block">Vat Number&nbsp;</Label>
                        <div>
                            <Input
                                style={{
                                    width: "300px",
                                }}
                                value={vatNumber}
                                readOnly={view === "View"}
                                placeholder={"Enter VAT Number"}
                                onChange={(e) => {
                                    setVatNumber(e.target.value);
                                }}
                            />
                        </div>
                    </div>
                    <div className="mt-3">
                        <Label className="mb-1 d-block">Vat Code&nbsp;</Label>
                        <ComboBox
                            data={vatsdata}
                            style={{
                                width: "300px",
                            }}
                            textField="display"
                            filterable={true}
                            onFilterChange={filterChange}
                            placeholder={"Search VAT Code"}
                            value={vatCodeId}
                            iconClassName="wa"
                            loading={vatsLoading}
                            disabled={view === "View"}
                            onChange={(e) => {
                                setVatCodeId(e.value);
                            }}
                        />
                    </div>
                    <div className="mt-3">
                        <Label className="mb-1 d-block">Branch&nbsp;</Label>
                        <ComboBox
                            data={branchData}
                            style={{
                                width: "300px",
                            }}
                            textField="branchName"
                            dataItemKey="branchId"
                            iconClassName="wa"
                            filterable={true}
                            onFilterChange={branchFilterChange}
                            placeholder={
                                branchesLoading ? "Loading branches..." : "Search Branch"
                            }
                            loading={branchesLoading}
                            disabled={view === "View"}
                            onChange={(e) => {
                                if (view === "View") {
                                    return;
                                }
                                setSelectedBranch(e.value);
                            }}
                            value={selectedBranch}
                        />
                    </div>
                </div>
                <div className="ms-2">
                    <div className="mt-3">
                        <Label className="mb-1">Credit Limit&nbsp;</Label>
                        <div>
                            <Input
                                value={creditLimit}
                                readOnly={view === "View"}
                                style={{ minWidth: "300px" }}
                                placeholder={"Enter Limit"}
                                type="number"
                                onChange={(e) => {
                                    setCreditLimit(e.target.value);
                                }}
                            />
                        </div>
                    </div>

                    <div className="mt-3">
                        <Label className="mb-1 d-block">Default Nominal&nbsp;</Label>
                        <ComboBox
                            data={filteredNominals}
                            style={{
                                width: "300px",
                            }}
                            textField="display"
                            dataItemKey="nominalRef"
                            disabled={view === "View"}
                            filterable={true}
                            onFilterChange={nominalsFilterChange}
                            placeholder={"Search Default Nominal"}
                            value={selectedNominal}
                            iconClassName="wa"
                            loading={nominalsLoading}
                            readOnly={view === "View"}
                            onChange={(e) => {
                                if (view === "View") return;
                                setSelectedNominal(e.value);
                            }}
                        />
                    </div>
                    <Label className="mb-1 mt-3 d-block">Payment Terms&nbsp;</Label>
                    <div className="mt-3 d-flex align-items-center">
                        <Label className="mb-1 ms-3 me-2 w-50">Due in Days&nbsp;</Label>
                        <Input
                            value={termDays}
                            readOnly={view === "View"}
                            type="number"
                            min="0"
                            placeholder={"Enter Due in Days"}
                            onChange={(e) => {
                                handleTermDaysChange(e.target.value);
                            }}
                        />
                    </div>
                    <div className="mt-2">
                        <div className="d-flex radio fs-7  ms-3">
                            <Checkbox
                                defaultChecked={false}
                                value={payWhenPaid}
                                onClick={changePayWhenPaid}
                                label={"Pay When Paid"}
                            />
                        </div>
                    </div>
                </div>
            </div>
            <Form
                initialValues={address}
                render={() => (
                    <AddressInputComponent
                        name="Address"
                        readOnly={view === "View"}
                        address={address}
                        setAddress={setAddress}
                        width={"300px"}
                        contactComponent={`col-5 me-5`}
                    />
                )}></Form>
        </div>
    );

    const BankInfoTab = () => (
        <div className="d-flex" style={{ gap: "10%" }}>
            <div>
                <div className="mt-3">
                    <Label className="mb-1 d-block">Account Title&nbsp;</Label>
                    <Input
                        style={{ width: "300px" }}
                        value={bankAccount.accountTitle}
                        readOnly={view === "View"}
                        placeholder="Enter Account Title"
                        onChange={(e) => updateBankAccount("accountTitle", e.target.value)}
                    />
                </div>
                <div className="mt-3">
                    <Label className="mb-1 d-block">Sort Code&nbsp;</Label>
                    <Input
                        style={{ width: "300px" }}
                        value={bankAccount.sortCode}
                        readOnly={view === "View"}
                        placeholder="Enter Sort Code"
                        onChange={(e) => updateBankAccount("sortCode", e.target.value)}
                    />
                </div>
                <div className="mt-3">
                    <Label className="mb-1 d-block">Account Number&nbsp;</Label>
                    <Input
                        style={{ width: "300px" }}
                        value={bankAccount.accountNo}
                        readOnly={view === "View"}
                        placeholder="Enter Account Number"
                        onChange={(e) => updateBankAccount("accountNo", e.target.value)}
                    />
                </div>
                <div className="mt-3">
                    {/* <div className="d-flex radio">
                        <Checkbox
                            value={bankAccount.bacsPayment}
                            disabled={view === "View"}
                            onClick={() => {
                                if (view === "View") return;
                                updateBankAccount("bacsPayment", !bankAccount.bacsPayment);
                            }}
                            label="BACS Payment"
                        />
                    </div> */}
                </div>
                <div className="mt-3">
                    <Label className="mb-1 d-block">BACS Code&nbsp;</Label>
                    <Input
                        style={{ width: "300px" }}
                        value={bankAccount.bacsCode}
                        readOnly={view === "View"}
                        placeholder="Enter BACS Code"
                        onChange={(e) => updateBankAccount("bacsCode", e.target.value)}
                    />
                </div>
            </div>
            <div>
                <div className="mt-3">
                    <Label className="mb-1 d-block">Account Type&nbsp;</Label>
                    <Input
                        style={{ width: "300px" }}
                        value={bankAccount.accountType}
                        readOnly={view === "View"}
                        placeholder="Enter Account Type"
                        onChange={(e) => updateBankAccount("accountType", e.target.value)}
                    />
                </div>
                <div className="mt-3">
                    <Label className="mb-1 d-block">Bank Name&nbsp;</Label>
                    <Input
                        style={{ width: "300px" }}
                        value={bankAccount.bankName}
                        readOnly={view === "View"}
                        placeholder="Enter Bank Name"
                        onChange={(e) => updateBankAccount("bankName", e.target.value)}
                    />
                </div>
                <div className="mt-3">
                    <Label className="mb-1 d-block">IBAN&nbsp;</Label>
                    <Input
                        style={{ width: "300px" }}
                        value={bankAccount.iban}
                        readOnly={view === "View"}
                        placeholder="Enter IBAN"
                        onChange={(e) => updateBankAccount("iban", e.target.value)}
                    />
                </div>
            </div>
        </div>
    );

    const newSupplier = (
        <div className="">
            <Dialog
                title={`${view} ${props.type === "Consultant" ? "Consultant" : "Supplier"}`}
                onClose={props.onClose}
                className="add-dialog dialog-md">
                {props.viewType !== "Add" && isLoadingSupplier ? (
                    <div
                        className="d-flex justify-content-center align-items-center"
                        style={{ minHeight: "500px" }}>
                        <Loader type="infinite-spinner" size="large" />
                    </div>
                ) : (
                    <TabStrip
                        keepTabsMounted={true}
                        selected={selected}
                        onSelect={(e) => setSelected(e.selected)}
                        className="mt-4 px-5 pb-4 pagetab companytab w-100">
                        {tabs.map((item, index) => {
                            return (
                                <TabStripTab
                                    title={
                                        <PageTabItemTitle
                                            title={item}
                                            index={tabs.indexOf(item)}
                                            selected={selected}
                                        />
                                    }
                                    key={index}>
                                    <div className={`${item === "Details" ? "mt-4" : "mt-2"} w-100`}>
                                        {item === "Details" && DetailsTab()}
                                        {item === "Defaults" && DefaultsTab()}
                                        {item === "Bank Info" && BankInfoTab()}
                                        {item === "Documents" && (
                                            <DocumentList
                                                supplierId={supplierId}
                                                supplierRef={supplierRef}
                                                tileHeading="Documents"
                                                viewMode={view === "View"}
                                                component="supplier"
                                                className="p-1 container-fluid"
                                            />
                                        )}
                                    </div>
                                </TabStripTab>
                            );
                        })}
                    </TabStrip>
                )}
                <DialogActionsBar>
                    <div className="dialog-footer">
                        <Button
                            className={`k-button common-btn bgBtnSecondary me-3`}
                            onClick={props.onClose}
                            type="button">
                            {view === "View" ? "Close" : "Cancel"}
                        </Button>
                        {view !== "View" && canEditSupplier && (
                            <SubmitButton
                                className="me-3"
                                onClick={submitSupplier}
                                isLoading={isSubmitting}>
                                {view === "Add" ? "Save" : "Update"}
                            </SubmitButton>
                        )}
                        {view === "View" && canEditSupplier && (
                            <Button className="common-btn me-3" onClick={() => setView("Edit")}>
                                Edit
                            </Button>
                        )}
                    </div>
                </DialogActionsBar>
            </Dialog>
        </div>
    );
    return <div className="gridMargin">{newSupplier}</div>;
};
export default AddNewSupplier;
